import makeTree from 'utils/makeTree';

// contabilidad/clasificaciones-de-fuentes-de-financiamiento/ (SIN "-interna") ya entrega, del
// lado del backend, el catálogo nacional (rubro > tipo > fuente) combinado con las fuentes
// "interno" de la entidad en un solo listado autorreferenciado por `clave_padre_id` — es la
// misma fuente que usa la tabla principal de src/views/Contabilidad/FuenteDeFinanciamiento.jsx
// para arbolar correctamente. El endpoint "-interna/" por sí solo NO trae la jerarquía nacional,
// solo las fuentes propias de la entidad (por eso el árbol salía plano, con un solo nivel).
//
// Igual que con el Clasificador por Objeto del Gasto, el `id` numérico puede repetirse entre
// nodos nacionales e internos (son tablas distintas con sus propios autoincrementales), así que
// se aplica el mismo truco: los nodos NO seleccionables (nacionales) se convierten a `id` string
// antes de armar el árbol; las hojas seleccionables (internas) conservan su `id` numérico.
export const makeTreeFromFuentesDeFinanciamiento = (data) => makeTree(
  (data || [])
    .map((item) => (!item.seleccionable ? { ...item, id: `${item.id}` } : item))
    .sort((a, b) => a.id - b.id),
);

const buildDisabledTree = (data = []) => data.map((item) => {
  const clone = { ...item };
  const children = clone.children?.length && typeof clone.id === 'string'
    ? buildDisabledTree(clone.children) : [];
  if (!children.length || typeof clone.id === 'number') {
    delete clone.children;
    return ({ ...clone, disabled: !clone.seleccionable });
  }
  return ({ ...clone, children, disabled: !clone.seleccionable });
});

// El `id` crudo se repite entre nodos nacionales e internos (ver arriba), así que no sirve como
// `key`/`value` único de TreeSelect. En su lugar se usa una ruta que encierra todo el linaje
// (`root-<id>-<índice>` por nivel), garantizando unicidad sin importar de qué tabla venga el nodo.
// El `id` real se conserva aparte para poder localizar el nodo por id (columnas, edición, etc.)
// y `extractIdFuenteDeFinanciamiento` recupera el id real a partir del value al guardar.
const buildPathedTree = (data, parentPath = 'root') => {
  if (!data?.length) return [];
  return data.map((item, index) => {
    const clone = { ...item };
    const currentPath = `${parentPath}-${clone.id}-${index}`;
    const children = buildPathedTree(clone.children, currentPath);
    const esHoja = !children.length;
    return {
      key: currentPath,
      value: currentPath,
      id: clone.id,
      title: `${clone.clave} - ${clone.nombre}`,
      disabled: esHoja ? clone.disabled : true,
      ...(children.length && { children }),
    };
  });
};

export const normalizeFuentesDeFinanciamiento = (tree) => buildPathedTree(
  buildDisabledTree(tree),
);

// Solo funciona sobre values generados por normalizeFuentesDeFinanciamiento
// (`root-<id>-<índice>-...-<id>-<índice>`): el id real es el penúltimo segmento.
export const extractIdFuenteDeFinanciamiento = (value) => {
  const extract = (v) => {
    if (typeof v !== 'string' || !v.includes('-')) return v;
    const parts = v.split('-');
    return parts[parts.length - 2];
  };
  return Array.isArray(value) ? value.map(extract) : extract(value);
};

export default makeTreeFromFuentesDeFinanciamiento;
